import {
    Avatar,
    Box,
    Button,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Stack,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';
import { useSelector } from 'react-redux';
import Alert from '/@/components/bayer/alert';
import ModalV2 from '/@/components/bayer/modal-v2';
import { CHIP_COLORS } from '/@/components/chip-label';
import settings from '/@/core/settings';
import {
    useAdvisorRvLinks,
    useAncestors,
    useGetUserNode,
    useOwnUserNode,
    useUpdateUserNode,
    useUserNodes,
} from '/@/hooks/nodes';
import { useIsValidatedAdvisor, useIsValidatedRV } from '/@/hooks/session';
import { _ } from '/@/locale';
import { getSessionCountry } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import { getInitials } from '/@/utils';
import AREA_CODES from '/@/utils/area-codes';
import { formatPhoneNumber, validatePhoneNumber } from '/@/utils/phone-numbers';
import { validateGrowerEmail } from '../new-growers-modal-v2/add-grower-section/grower-form';
import FieldviewSection from './fieldview-section';
import TeamAccordion, { type GrowerTeamRow } from './team-accordion';

const AvatarType = ({ type, name }: { type: 'consultant' | 'advisor' | 'rv'; name: string }) => {
    return (
        <Avatar
            sx={{
                background: CHIP_COLORS[type as keyof typeof CHIP_COLORS]?.background,
                position: 'relative',
                overflow: 'visible',
            }}
        >
            <Typography
                variant='subtitle2'
                fontWeight='bold'
                color={CHIP_COLORS[type as keyof typeof CHIP_COLORS]?.color}
            >
                {getInitials(name)}
            </Typography>
        </Avatar>
    );
};

const FieldGrowerModal = ({
    open,
    onClose,
    growerNodeId,
}: {
    open: boolean;
    onClose: () => void;
    growerNodeId: number;
}) => {
    const { data: grower } = useGetUserNode(growerNodeId);
    const rvUserQuery = useOwnUserNode();
    const allGrowersQuery = useUserNodes({
        node: rvUserQuery.data?.id,
        page: 1,
        page_size: 10000,
        type: 'grower',
    });
    const theme = useTheme();
    const country = useSelector(getSessionCountry);

    const isRV = useIsValidatedRV();
    const isAdvisor = useIsValidatedAdvisor();

    const ancestorsQuery = useAncestors({ node: growerNodeId });
    const advisorRvLinksQuery = useAdvisorRvLinks();
    const hasLinkedRV = (advisorRvLinksQuery.data?.results?.length ?? 0) > 0;
    const canEditSubtype = isAdvisor && (grower?.subtype === 'common' || !hasLinkedRV);
    const hasTeam = grower?.subtype === 'premium';

    const teamData = useMemo((): GrowerTeamRow[] => {
        if (!grower || !ancestorsQuery.data || !hasTeam) return [];

        const rows: GrowerTeamRow[] = [];

        const distributor = ancestorsQuery.data?.find((a) => a.type === 'other' && a.name);
        const squadName = ancestorsQuery.data?.find((a) => a?.type === 'squad')?.unassignedUserData?.name || '';
        const rvParent = ancestorsQuery.data?.find((n) => n?.type === 'rv');
        const advisorParent = ancestorsQuery.data?.find((n) => n?.type === 'advisor');
        const consultantParent = ancestorsQuery.data?.find((n) => n?.type === 'consultant');

        if (consultantParent) {
            rows.push({
                ...consultantParent,
                distributorOrSquad: squadName || '',
                role: _('Consultor'),
            });
        }

        if (isRV && advisorParent) {
            rows.push({
                ...advisorParent,
                distributorOrSquad: distributor?.name || '',
                role: _('Asesor'),
            });
        }

        if (isAdvisor && rvParent) {
            rows.push({
                ...rvParent,
                distributorOrSquad: squadName || '',
                role: 'RV',
            });
        }

        return rows;
    }, [grower, ancestorsQuery.data, isRV, isAdvisor, hasTeam]);

    const form = useForm<{
        cuit: string | undefined;
        subtype: string;
        name: string;
        email: string;
        phone: string | undefined;
        location: string | undefined;
    }>({
        mode: 'onChange',
        defaultValues: {
            cuit: undefined,
            subtype: '',
            name: '',
            email: '',
            phone: undefined,
            location: undefined,
        },
    });

    useEffect(() => {
        if (grower) {
            form.reset({
                cuit: grower?.cuit ?? undefined,
                subtype: grower?.subtype ?? '',
                name: grower?.unassignedUserData?.name ?? '',
                email: grower?.unassignedUserData?.email ?? '',
                phone:
                    grower?.unassignedUserData?.phone && grower?.unassignedUserData?.phone !== ''
                        ? formatPhoneNumber(grower.unassignedUserData.phone)
                        : undefined,
                location:
                    grower?.unassignedUserData?.location && grower?.unassignedUserData?.location !== ''
                        ? grower?.unassignedUserData?.location
                        : undefined,
            });
        }
    }, [open, grower]);

    const updateUserNodeMutation = useUpdateUserNode();

    const onSubmit = async (data: {
        cuit: string | undefined;
        subtype: string;
        name: string;
        email: string;
        phone: string | undefined;
        location: string | undefined;
    }) => {
        try {
            updateUserNodeMutation.trigger({
                id: growerNodeId,
                data: {
                    subtype: data.subtype as 'common' | 'premium',
                    unassignedUserData: {
                        name: data.name,
                        email: data.email,
                        phone: data.phone,
                        location: data.location,
                    },
                },
            });
            mpTrackEvent('Grower edited', undefined);
            enqueueSnackbar(_('Productor actualizado correctamente'), { variant: 'success' });
            onClose();
        } catch (error) {
            enqueueSnackbar(_('Error al actualizar el productor'), { variant: 'error' });
        }
    };

    const growerFieldviewData = grower?.user;
    console.log(updateUserNodeMutation.isMutating, !form.formState.isDirty, !form.formState.isValid);

    return (
        <ModalV2
            open={open}
            onClose={onClose}
            title={_('Información del productor')}
            maxWidth='md'
            bodyProps={{ sx: { p: 3, maxHeight: '80vh', overflow: 'auto' } }}
            actions={
                <Stack direction='row' spacing={1}>
                    <Button
                        type='button'
                        variant='text'
                        color='secondary'
                        onClick={onClose}
                        disabled={updateUserNodeMutation.isMutating}
                    >
                        {_('Cancelar')}
                    </Button>
                    <Button
                        type='submit'
                        variant='contained'
                        color='secondary'
                        disabled={
                            updateUserNodeMutation.isMutating || !form.formState.isDirty || !form.formState.isValid
                        }
                        loading={updateUserNodeMutation.isMutating}
                        onClick={form.handleSubmit(onSubmit)}
                    >
                        {_('Guardar cambios')}
                    </Button>
                </Stack>
            }
            actionsProps={{
                justifyContent: 'end',
            }}
        >
            <FormProvider {...form}>
                <Stack className='mp-mask' spacing={3} direction='column' sx={{ width: '100%', pb: 7 }}>
                    <Typography variant='subtitle1' color='primary' sx={{ textAlign: 'start', width: '100%' }}>
                        {_(
                            'Mantené actualizada la información personal del productor y sus datos vinculados en {{appName}}.',
                            { appName: settings.app.name }
                        )}
                    </Typography>
                    <Typography
                        variant='subtitle1'
                        fontWeight='bold'
                        color='primary'
                        sx={{ textAlign: 'start', width: '100%' }}
                    >
                        {_('Datos registrados')}
                    </Typography>
                    {isAdvisor && (
                        <Alert severity='blue'>
                            <Typography color='primary' variant='body1' sx={{ textAlign: 'left' }}>
                                <Box component='span' fontWeight='bold'>
                                    {_('Importante: ')}
                                </Box>
                                {_(
                                    'Si cambias el segmento a Premium, los datos del productor van a ser compartidos con el RV al cual estés vinculado. Una vez realizado el cambio, no podrás revertirlo.'
                                )}
                            </Typography>
                        </Alert>
                    )}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                            gap: 2,
                            width: '100%',
                        }}
                    >
                        <Controller
                            name='cuit'
                            control={form.control}
                            render={({ field }) => (
                                <TextField {...field} value={field.value ?? ''} label={_('CUIT')} fullWidth disabled />
                            )}
                        />

                        <Controller
                            name='subtype'
                            control={form.control}
                            rules={{ required: _('El tipo es requerido') }}
                            render={({ field, fieldState: { error } }) => (
                                <FormControl fullWidth error={!!error} disabled={!canEditSubtype}>
                                    <InputLabel>{_('Segmento')}</InputLabel>
                                    <Select
                                        {...field}
                                        label={_('Segmento')}
                                        sx={{ textAlign: 'left' }}
                                        renderValue={(value) =>
                                            value === 'premium' ? _('Premium') : _('Otros segmentos')
                                        }
                                    >
                                        <MenuItem value='premium'>
                                            <RadioGroup>
                                                <Radio
                                                    value='premium'
                                                    color='secondary'
                                                    checked={field.value === 'premium'}
                                                    onChange={field.onChange}
                                                />
                                            </RadioGroup>
                                            {_('Premium')}
                                        </MenuItem>
                                        <MenuItem value='common'>
                                            <RadioGroup>
                                                <Radio
                                                    value='common'
                                                    color='secondary'
                                                    checked={field.value === 'common'}
                                                    onChange={field.onChange}
                                                />
                                            </RadioGroup>
                                            {_('Otros segmentos')}
                                        </MenuItem>
                                    </Select>
                                    {error && <FormHelperText>{error.message}</FormHelperText>}
                                </FormControl>
                            )}
                        />

                        <Controller
                            name='name'
                            control={form.control}
                            rules={{ required: _('El nombre es requerido') }}
                            render={({ field, fieldState: { error } }) => (
                                <TextField
                                    {...field}
                                    label={_('Nombre')}
                                    fullWidth
                                    error={!!error}
                                    helperText={error?.message}
                                />
                            )}
                        />

                        <Controller
                            name='location'
                            control={form.control}
                            rules={{ required: _('La ubicación es requerida') }}
                            render={({ field, fieldState: { error } }) => (
                                <TextField
                                    {...field}
                                    label={_('Ubicación')}
                                    fullWidth
                                    error={!!error}
                                    helperText={error?.message}
                                />
                            )}
                        />

                        <Controller
                            name='email'
                            control={form.control}
                            rules={{
                                required: _('El mail es requerido'),
                                validate: (v) => {
                                    return validateGrowerEmail(
                                        v,
                                        allGrowersQuery.data?.results || [],
                                        rvUserQuery.data,
                                        undefined,
                                        undefined,
                                        growerNodeId
                                    );
                                },
                            }}
                            render={({ field, fieldState: { error } }) => (
                                <TextField
                                    {...field}
                                    label={_('Mail')}
                                    fullWidth
                                    error={!!error}
                                    helperText={error?.message}
                                />
                            )}
                        />

                        <Controller
                            control={form.control}
                            name='phone'
                            rules={{
                                validate: (v) => {
                                    if (!v || v === '') return true;
                                    return validatePhoneNumber(v);
                                },
                            }}
                            render={({ field, fieldState }) => {
                                return (
                                    <FormControl
                                        fullWidth
                                        sx={{
                                            height: 'auto',
                                        }}
                                    >
                                        <PhoneInput
                                            {...field}
                                            onChange={(value, data, event, formattedValue) => {
                                                field.onChange(formattedValue);
                                            }}
                                            buttonStyle={{
                                                border: 'none',
                                            }}
                                            specialLabel=''
                                            country={country!.code!.toLowerCase()}
                                            inputStyle={{
                                                border: 'none',
                                                height: '100%',
                                                borderRadius: '0',
                                                width: '100%',
                                            }}
                                            containerStyle={{
                                                border: '1px solid #0000003b',
                                                borderRadius: '0',
                                            }}
                                            enableAreaCodeStretch
                                            enableAreaCodes
                                            areaCodes={AREA_CODES}
                                            masks={{ mx: '... .... ....' }}
                                        />
                                        <FormHelperText
                                            sx={{
                                                fontSize: 9,
                                                color: fieldState.error
                                                    ? theme.palette.error.main
                                                    : theme.palette.primary.main,
                                            }}
                                        >
                                            {fieldState.error
                                                ? _('El formato del número de teléfono es incorrecto.')
                                                : ''}
                                        </FormHelperText>
                                    </FormControl>
                                );
                            }}
                        />
                    </Box>
                    {!!growerFieldviewData && <FieldviewSection growerFieldviewData={growerFieldviewData} />}
                    {hasTeam && <TeamAccordion teamData={teamData} />}
                </Stack>
            </FormProvider>
        </ModalV2>
    );
};

export default FieldGrowerModal;
